import { tt } from '@agos/shared'

/**
 * The marketing editor's media library - `SAAS.md §3.101`.
 *
 * **The endpoint was built for this and had no caller.** `POST /api/v1/upload-media`'s own javadoc
 * says *"Upload anything the marketing library takes… The Control Tower token is checked here rather
 * than left to the ordinary guard"* - a whole auth path added to one endpoint for the Tower, and the
 * screen that was the reason for it did not exist. Legacy's editor has the panel; every marketing
 * section that shows a picture takes an image **URL as a text field**, so without a library a
 * photograph has to be uploaded somewhere else and pasted in.
 *
 * Everything here is the part worth testing: what the list holds, what survives a hostile stored
 * value, and what the cap does. The upload itself is a `customFetch` with a real `FormData` in the
 * page - **the generated `postUploadMedia` cannot do it**, because orval types a file part as a
 * `Blob` field and emits `JSON.stringify(body)` with `Content-Type: application/json` against a
 * multipart endpoint.
 */
export interface MediaItem {
  /** A path under `/api/uploads` for anything uploaded here - see `isOwnUpload`. */
  url: string
  kind: 'image' | 'video'
  name: string
  /** Epoch milliseconds, so the newest sorts first without parsing anything. */
  addedAt: number
}

/** Legacy's own key, so an operator who used the old Tower keeps their list. */
export const MEDIA_LIBRARY_KEY = 'hk_media_library'

/**
 * Legacy's cap.
 *
 * It is a convenience list rather than an archive - the files are on the server either way, and the
 * cap is what stops `localStorage` growing without bound in a tab nobody reloads.
 */
export const MEDIA_LIBRARY_LIMIT = 50

/**
 * The stored list, or an empty one.
 *
 * **Never throws.** `localStorage` can be absent, blocked or hold something that is not this shape,
 * and a marketing editor that renders nothing because a browser refused site data is a worse outcome
 * than an empty library.
 */
export function readLibrary(storage?: Pick<Storage, 'getItem'>): MediaItem[] {
  try {
    const store = storage ?? window.localStorage
    const raw = store.getItem(MEDIA_LIBRARY_KEY)
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.flatMap((entry) => {
      if (typeof entry !== 'object' || entry === null) return []
      const row = entry as Record<string, unknown>
      const url = typeof row.url === 'string' ? row.url.trim() : ''
      if (url === '') return []
      return [
        {
          url,
          kind: row.kind === 'video' ? 'video' : 'image',
          name: typeof row.name === 'string' ? row.name : url,
          addedAt: typeof row.addedAt === 'number' && Number.isFinite(row.addedAt) ? row.addedAt : 0,
        },
      ]
    })
  } catch {
    return []
  }
}

/** Stores the list, capped, and says nothing when the browser refuses. */
export function writeLibrary(items: readonly MediaItem[], storage?: Pick<Storage, 'setItem'>): void {
  try {
    const store = storage ?? window.localStorage
    store.setItem(MEDIA_LIBRARY_KEY, JSON.stringify(items.slice(0, MEDIA_LIBRARY_LIMIT)))
  } catch {
    // A private window or blocked site data. The upload still happened and the URL is on screen.
  }
}

/**
 * The list with one upload added, newest first.
 *
 * **Re-uploading the same file must not produce two rows.** The server names a stored file with a
 * random suffix, so two uploads of one picture give two URLs and two rows - correct, they are two
 * files. What this de-duplicates is the same **URL** arriving twice, which is what a double-click on
 * the file input produces.
 */
export function withUpload(items: readonly MediaItem[], added: MediaItem): MediaItem[] {
  return [added, ...items.filter((item) => item.url !== added.url)].slice(0, MEDIA_LIBRARY_LIMIT)
}

/** The list without one entry. The file is untouched on the server - see `removeNote`. */
export function withoutUrl(items: readonly MediaItem[], url: string): MediaItem[] {
  return items.filter((item) => item.url !== url)
}

/**
 * What an upload's response becomes, or `null`.
 *
 * `kind` is the server's - it decides by `Content-Type` and is the only side that has seen the
 * bytes. An empty `url` is the one failure a 200 can still carry, and legacy checks for it too
 * (*"Server vrátil prázdnu URL"*).
 */
export function itemOfUpload(
  response: { url?: unknown; kind?: unknown } | null | undefined,
  filename: string,
  uploadedAt: number,
): MediaItem | null {
  const url = typeof response?.url === 'string' ? response.url.trim() : ''
  if (url === '') return null
  return {
    url,
    kind: response?.kind === 'video' ? 'video' : 'image',
    name: filename.trim() === '' ? url : filename.trim(),
    addedAt: uploadedAt,
  }
}

/**
 * Whether a URL is one of ours, which is the only case the copy has a decision about.
 *
 * **What this is standing in for.** Legacy's copy button resolves the path against
 * `window.location.origin` before copying; AGOS copies it as it came, and the reason is written here
 * because an identity function would have been an untestable place to put it. In AGOS that
 * absolutisation would paste `https://hkd.toxik.space/api/uploads/x.webp` into a section of the
 * **public** site - the admin domain serving the public site's pictures, which works and is wrong:
 * it couples one to the other, and the images break the day the Tower is firewalled. The marketing
 * app proxies `/api` itself in both `dev` and `preview`, and Nginx serves it per domain in
 * production, so the relative path is right on every domain. `UploadDtos` says the same: *"A path
 * under `/api/uploads`, not an absolute URL."*
 *
 * The *absence* of that transformation is asserted off the page's source in `media-library.test.ts`,
 * because a function that returns its argument cannot be tested for what it does not do.
 */
export function isOwnUpload(url: string): boolean {
  return url.startsWith('/api/uploads/')
}

/**
 * The note under a URL that is not ours.
 *
 * An operator can paste a CDN link into a section by hand, and such a row in the list is fine - but
 * it is not a file this platform can account for, and *remove from the list* means something
 * different for it: there is no server copy to leave behind.
 */
export function foreignUrlNote(url: string): string {
  return isOwnUpload(url) ? '' : tt('Externá adresa - nie je to súbor na tomto serveri.')
}

/** Said in the panel, because the files and the list are not stored in the same place. */
export function libraryScopeNote(): string {
  return tt('Zoznam je uložený v tomto prehliadači - súbory sú na serveri a vidí ich každý.')
}

/**
 * Said on the remove button, because it does not delete anything.
 *
 * Named `mediaRemoveNote` rather than `removeNote`: `marketing.ts` beside this already exports one,
 * about removing a **section**, and the two would have collided in the page that imports both.
 */
export function mediaRemoveNote(): string {
  return tt('Odobrať zo zoznamu - súbor na serveri zostáva')
}

/** Said when the library is empty, which is its ordinary first state. */
export function emptyLibraryNote(): string {
  return tt('Nahrajte súbor a skopírujte jeho adresu do ktorejkoľvek sekcie.')
}
